import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Save, Eye, EyeOff } from "lucide-react";
import { formatApiError } from "@/lib/errors";

export default function AdminPaymentSettings() {
  const [data, setData] = useState(null);
  const [stripeKey, setStripeKey] = useState("");
  const [paystackKey, setPaystackKey] = useState("");
  const [showStripe, setShowStripe] = useState(false);
  const [showPaystack, setShowPaystack] = useState(false);

  const load = () =>
    api.get("/admin/settings/payment").then(({ data }) => setData(data)).catch(() => {});

  useEffect(() => { load(); }, []);

  const save = async (patch, label) => {
    try {
      const { data: fresh } = await api.post("/admin/settings/payment", patch);
      setData(fresh);
      toast.success(`${label} saved`);
      setStripeKey("");
      setPaystackKey("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Save failed"));
    }
  };

  const toggle = (which, value) => {
    const key = `${which}_enabled`;
    save({ [key]: value }, `${which} ${value ? "enabled" : "disabled"}`);
  };

  if (!data) return <div className="p-8 text-gray-500">Loading...</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-10">
      <div className="overline">System</div>
      <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-white mt-2 mb-3">
        Payment settings
      </h1>
      <p className="text-gray-400 max-w-2xl mb-8">
        Configure and toggle payment providers. Keys are stored securely and never exposed on the frontend.
      </p>

      <div className="space-y-4">
        {/* PAYSTACK */}
        <ProviderCard
          title="Paystack"
          subtitle="Cards · Bank · USSD · Transfer · NGN native"
          status={data.paystack}
          onToggle={(v) => toggle("paystack", v)}
          testid="prov-paystack"
        >
          <div>
            <Label className="text-gray-300">Secret key</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                data-testid="paystack-key-input"
                type={showPaystack ? "text" : "password"}
                value={paystackKey}
                onChange={(e) => setPaystackKey(e.target.value)}
                placeholder={data.paystack.configured ? `Current: ${data.paystack.masked_key}` : "sk_test_..."}
                className="bg-[#050914] border-white/10 focus:border-[#D4AF37] font-mono"
              />
              <button type="button" onClick={() => setShowPaystack((v) => !v)} className="text-gray-400 hover:text-white">
                {showPaystack ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <Button
                disabled={!paystackKey.trim()}
                onClick={() => save({ paystack_secret_key: paystackKey }, "Paystack key")}
                className="btn-gold rounded-full h-10"
                data-testid="paystack-save"
              >
                <Save className="w-3 h-3 mr-1" /> Save
              </Button>
            </div>
            <div className="text-[11px] text-gray-500 mt-1.5">
              Get it from dashboard.paystack.com → Settings → API Keys & Webhooks
            </div>
          </div>
        </ProviderCard>

        {/* STRIPE */}
        <ProviderCard
          title="Stripe"
          subtitle="International cards · USD/NGN accepted"
          status={data.stripe}
          onToggle={(v) => toggle("stripe", v)}
          testid="prov-stripe"
        >
          <div>
            <Label className="text-gray-300">API key</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                data-testid="stripe-key-input"
                type={showStripe ? "text" : "password"}
                value={stripeKey}
                onChange={(e) => setStripeKey(e.target.value)}
                placeholder={data.stripe.configured ? `Current: ${data.stripe.masked_key}` : "sk_live_... or sk_test_..."}
                className="bg-[#050914] border-white/10 focus:border-[#D4AF37] font-mono"
              />
              <button type="button" onClick={() => setShowStripe((v) => !v)} className="text-gray-400 hover:text-white">
                {showStripe ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <Button
                disabled={!stripeKey.trim()}
                onClick={() => save({ stripe_api_key: stripeKey }, "Stripe key")}
                className="btn-gold rounded-full h-10"
                data-testid="stripe-save"
              >
                <Save className="w-3 h-3 mr-1" /> Save
              </Button>
            </div>
            <div className="text-[11px] text-gray-500 mt-1.5">
              Get it from dashboard.stripe.com → Developers → API keys
            </div>
          </div>
        </ProviderCard>

        {/* BANK TRANSFER */}
        <ProviderCard
          title="Bank transfer"
          subtitle="Manual admin verification of Zenith Bank deposits"
          status={{ configured: true, enabled: data.bank_transfer.enabled }}
          onToggle={(v) => toggle("bank_transfer", v)}
          testid="prov-bank"
        >
          <div className="text-sm text-gray-400">
            Investors submit teller receipts on <span className="text-white">/bank-transfer</span>. Admin reviews at <span className="text-white">/admin/bank-transfers</span>.
          </div>
        </ProviderCard>
      </div>
    </div>
  );
}

function ProviderCard({ title, subtitle, status, onToggle, children, testid }) {
  return (
    <div className="bp-card rounded-2xl p-6" data-testid={testid}>
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="font-heading text-2xl text-white">{title}</div>
          <div className="text-xs text-gray-500 mt-1">{subtitle}</div>
          <div className="mt-3 flex items-center gap-3 text-xs">
            <span className={`px-2 py-0.5 rounded-full ${status.configured ? "bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/30" : "bg-white/5 text-gray-400 border border-white/10"}`}>
              {status.configured ? "Configured" : "Not configured"}
            </span>
            <span className={`px-2 py-0.5 rounded-full ${status.enabled ? "bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30" : "bg-white/5 text-gray-500 border border-white/10"}`}>
              {status.enabled ? "Live" : "Disabled"}
            </span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Switch checked={!!status.enabled} onCheckedChange={onToggle} data-testid={`${testid}-toggle`} />
          <span className="text-[10px] text-gray-500">Toggle</span>
        </div>
      </div>
      {children}
    </div>
  );
}
